// Per-study open-tab registry in localStorage. A tab that crashes never fires
// `beforeunload`, so entries carry a timestamp and are pruned once stale — otherwise
// isStudyOpen would pin a study "open" forever and its storage could never be evicted.
// Legacy entries (bare string ids, no timestamp) are treated as living: their age is
// unknown, so evicting is unsafe, and they disappear once tabs re-register in the new shape.

export const TAB_HEARTBEAT_MS = 15_000;
export const TAB_TTL_MS = 45_000;

type TabEntry = { id: string; ts: number };

const keyFor = (scopeId: string) => `active-tabs-${scopeId}`;

const parseEntries = (raw: string | null): TabEntry[] => {
  try {
    const parsed = JSON.parse(raw ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry): TabEntry[] => {
      if (typeof entry === 'string') return [{ id: entry, ts: NaN }]; // legacy shape → always living
      if (entry && typeof entry.id === 'string' && typeof entry.ts === 'number') return [entry];
      return [];
    });
  } catch {
    return [];
  }
};

// A non-finite ts marks a legacy entry whose age is unknown, so it is kept.
const isLiving = (entry: TabEntry, now: number) =>
  !Number.isFinite(entry.ts) || now - entry.ts < TAB_TTL_MS;

const livingEntries = (scopeId: string, now: number) =>
  parseEntries(localStorage.getItem(keyFor(scopeId))).filter((entry) => isLiving(entry, now));

const persist = (scopeId: string, entries: TabEntry[]) => {
  try {
    if (entries.length > 0) {
      localStorage.setItem(keyFor(scopeId), JSON.stringify(entries));
    } else {
      localStorage.removeItem(keyFor(scopeId));
    }
  } catch (error) {
    console.error('[activeTabs] Failed to persist active tabs:', error);
  }
};

/** How many non-stale tabs are registered as viewing the study. */
export const countLivingTabs = (scopeId: string, now = Date.now()): number =>
  livingEntries(scopeId, now).length;

/** Whether any non-stale tab is registered as viewing the study. */
export const hasLivingTab = (scopeId: string, now = Date.now()): boolean =>
  countLivingTabs(scopeId, now) > 0;

/** Registers or refreshes this tab's heartbeat, pruning stale siblings in the same write. */
export const registerTab = (scopeId: string, tabId: string, now = Date.now()) => {
  const entries = livingEntries(scopeId, now).filter((entry) => entry.id !== tabId);
  entries.push({ id: tabId, ts: now });
  persist(scopeId, entries);
};

/** Removes this tab and returns how many living tabs remain. */
export const unregisterTab = (scopeId: string, tabId: string, now = Date.now()): number => {
  const remaining = livingEntries(scopeId, now).filter((entry) => entry.id !== tabId);
  persist(scopeId, remaining);
  return remaining.length;
};
